import { useState, type FormEvent, type ReactNode } from "react";
import { Button } from "@repo/blacksmith-ui/button";
import { Input } from "@repo/blacksmith-ui/input";
import { Label } from "@repo/blacksmith-ui/label";
import { AnimatedBlacksmithLogoOneline } from "@/components/BlacksmithLogoSVG";
import { instanceBasePath } from "@/lib/named-org";
type LookupState = "idle" | "looking" | "not-found" | "failed";

/**
 * Wraps the github.com sign-in card with a one-line switch below it; once a
 * GitHub Enterprise user clicks it, the card is swapped for an instance-name
 * lookup that continues on `/enterprise/{slug}/login`.
 */
export function EnterpriseInstanceLookup({
  children
}: {
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [state, setState] = useState<LookupState>("idle");
  if (!open) {
    return <>
        {children}
        <p className="mt-6 w-[360px] text-center type-body-xs-regular text-neutral-500 dark:text-neutral-400">
          <Button type="button" variant="inline" onClick={() => setOpen(true)}>
            <span>
              <span className="underline underline-offset-2">Sign in</span> with
              enterprise instance
            </span>
          </Button>
        </p>
      </>;
  }
  const trimmed = name.trim();
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!trimmed) return;
    setState("looking");
    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/auth/instances/${encodeURIComponent(trimmed)}`);
      if (response.status === 404) {
        setState("not-found");
        return;
      }
      if (!response.ok) throw new Error("Instance lookup failed");
      window.location.assign(`${instanceBasePath(trimmed)}/login`);
    } catch {
      setState("failed");
    }
  };
  const error = state === "not-found" ? `No GitHub Enterprise instance named "${trimmed}".` : state === "failed" ? "Couldn't look that up. Try again." : null;
  return <>
      <form onSubmit={handleSubmit} className="w-[360px] flex flex-col gap-6 text-center rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-8">
        <div className="w-48 mx-auto py-4 mb-0 text-neutral-900 dark:text-blacksmith-yellow">
          <AnimatedBlacksmithLogoOneline />
        </div>
        <div className="-mt-4 flex flex-col text-neutral-800 dark:text-white">
          <span className="text-xl font-medium">Sign in</span>
          <span className="text-sm text-neutral-500 dark:text-neutral-400">
            to your GitHub Enterprise instance
          </span>
        </div>
        <div className="flex flex-col gap-2 text-left">
          <Label htmlFor="enterprise-instance-name" className="text-neutral-500 dark:text-neutral-400">
            Enterprise name
          </Label>
          <Input id="enterprise-instance-name" placeholder="acme" autoFocus autoCapitalize="off" autoCorrect="off" spellCheck={false} value={name} onChange={event_0 => {
          setName(event_0.target.value);
          setState("idle");
        }} aria-invalid={error !== null} aria-describedby={error ? "enterprise-instance-name-error" : undefined} />
          {error && <p id="enterprise-instance-name-error" role="alert" className="type-body-xs-regular text-red-500 dark:text-red-400">
              {error}
            </p>}
        </div>
        <Button type="submit" variant="default" disabled={!trimmed || state === "looking"}>
          Continue
        </Button>
      </form>
      <p className="mt-6 w-[360px] text-center type-body-xs-regular text-neutral-500 dark:text-neutral-400">
        Not on GitHub Enterprise?{" "}
        <Button type="button" variant="inline" onClick={() => setOpen(false)}>
          <span>
            <span className="underline underline-offset-2">Sign in</span> with
            GitHub.com
          </span>
        </Button>
      </p>
    </>;
}